// Именное приглашение на подбор вузов (решение владельца 12.10): пути,
// тексты страницы, превью ссылки, картинки и сообщения WhatsApp.
//
// Адреса — рядом с результатом подбора (/…/r/<token>): ru и en —
// /study-abroad/invite/<token>, az — /az/xaricde-tehsil/devet/<token>
// («dəvət» латиницей без диакритики, как «xaricde-tehsil»).

import { ABROAD_GUEST_START_PATH, type GuestGrade, type GuestLang } from "./guest";

export const INVITE_TTL_DAYS = 30;
/** Токен приглашения: 24 случайных байта в base64url = 32 символа. */
export const INVITE_TOKEN_RE = /^[A-Za-z0-9_-]{24,64}$/;

export function guestInvitePath(lang: GuestLang, token: string): string {
  const base: Record<GuestLang, string> = {
    ru: "/study-abroad/invite/",
    az: "/az/xaricde-tehsil/devet/",
    en: "/en/study-abroad/invite/",
  };
  return base[lang] + token;
}

/**
 * Старт подбора по приглашению: токен во фрагменте (#invite=…) — не уходит на
 * сервер и в логи; старт читает его, подставляет поля и убирает из адреса.
 */
export function guestInviteStartPath(lang: GuestLang, token: string): string {
  return `${ABROAD_GUEST_START_PATH[lang]}#invite=${token}`;
}
export const GUEST_INVITE_HASH_RE = /^#invite=([A-Za-z0-9_-]{24,64})$/;

/**
 * Версия картинок превью в адресе (?v=). Мессенджеры кэшируют картинку по
 * адресу: сменили макет — подняли число, и Facebook/WhatsApp/Telegram скачают
 * её заново. 2 — 25.10.2026, подпись «Azərbaycan» вместо «Bakı».
 */
export const OG_IMAGE_REV = 2;

/** Картинка превью по токену — рендер на лету, src/routes/api/og/invite/$token.ts. */
/** Адрес именной картинки. n — число стран: новое число — новый адрес, мессенджер перечитает. */
export function guestInviteOgImagePath(token: string, n?: number | null): string {
  return `/api/og/invite/${token}` + (n ? `?n=${n}&v=${OG_IMAGE_REV}` : `?v=${OG_IMAGE_REV}`);
}

/** Адрес общей картинки старта (рисуется на лету с числом стран). */
export function guestStartOgImagePath(lang: GuestLang, n?: number | null): string {
  return `/api/og/abroad-start/${lang}` + (n ? `?n=${n}&v=${OG_IMAGE_REV}` : `?v=${OG_IMAGE_REV}`);
}

/** Что страница и старт знают о приглашении. Почты нет только у тех, кому её не вписали. */
export type InviteView =
  | { found: false }
  | { found: true; expired: true; lang: GuestLang }
  | {
      found: true;
      expired: false;
      lang: GuestLang;
      firstName: string;
      lastName: string | null;
      grade: GuestGrade | null;
      email: string | null;
      /** Хост для абсолютных адресов превью: прод или сама превью-версия. */
      origin?: string;
    };

export const GUEST_INVITE_COPY: Record<
  GuestLang,
  {
    hello: (name: string) => string;
    title: string;
    facts: [string, string, string];
    free: string;
    start: string;
    expiredTitle: string;
    expiredBody: string;
    notFoundTitle: string;
    toStart: string;
    loading: string;
  }
> = {
  az: {
    hello: (n) => `Salam, ${n}!`,
    title: "Sənin üçün universitet seçimi",
    facts: ["~20 dəqiqə", "Sən yazırsan: orta bal, dil, büdcə", "Sən alırsan: universitetlər, xərc, son tarixlər"],
    free: "pulsuz",
    start: "Başla",
    expiredTitle: "Bu dəvətin müddəti bitib",
    expiredBody: "Narahat olma — universitet seçimini adi qaydada başlaya bilərsən.",
    notFoundTitle: "Dəvət tapılmadı",
    toStart: "Seçimə başla",
    loading: "Yüklənir…",
  },
  ru: {
    hello: (n) => `Привет, ${n}!`,
    title: "Подбор вузов для тебя",
    facts: ["~20 минут", "Ты пишешь: средний балл, язык, бюджет", "Ты получаешь: вузы, стоимость, сроки подачи"],
    free: "бесплатно",
    start: "Начать",
    expiredTitle: "Срок этой ссылки истёк",
    expiredBody: "Ничего страшного — подбор вузов можно начать обычным способом.",
    notFoundTitle: "Приглашение не найдено",
    toStart: "Начать подбор",
    loading: "Загрузка…",
  },
  en: {
    hello: (n) => `Hi ${n}!`,
    title: "Your university match",
    facts: ["~20 minutes", "You tell us: GPA, language, budget", "You get: universities, costs, deadlines"],
    free: "free",
    start: "Start",
    expiredTitle: "This invitation has expired",
    expiredBody: "No problem — you can start the university matching the usual way.",
    notFoundTitle: "Invitation not found",
    toStart: "Start matching",
    loading: "Loading…",
  },
};

/**
 * Превью ссылки в мессенджерах. Имени нет — общий заголовок старта.
 * {countries} — число открытых стран (withCountries, src/edu/countries-count.ts).
 */
export const GUEST_INVITE_OG: Record<GuestLang, { title: (name: string) => string; description: string }> = {
  az: {
    title: (n) => `Sənin üçün universitet seçimi, ${n}`,
    description: "Pulsuz · təxminən 20 dəqiqə · {countries} universitetləri: attestatla qəbul, xərc və son tarixlər",
  },
  ru: {
    title: (n) => `Подбор вузов для тебя, ${n}`,
    description: "Бесплатно · около 20 минут · вузы {countries}: поступление с аттестатом, стоимость и сроки",
  },
  en: {
    title: (n) => `Your university match, ${n}`,
    description: "Free · about 20 minutes · universities in {countries}: admission with your certificate, costs and deadlines",
  },
};

/**
 * Тексты именной картинки превью — стиль утверждён Джавидом 12.10 (тот же,
 * что у общей картинки старта): заголовок-вопрос с именем, подпись, янтарная
 * пилюля «Pulsuz · təxminən 20 dəqiqə». Без надписей капсом и без засечек.
 * question — заголовок общей карточки старта (без имени). В sub —
 * {countries}: число стран рисуется на лету, не зашито в картинку.
 */
export const GUEST_INVITE_IMAGE: Record<
  GuestLang,
  { title: (name: string) => string; question: string; sub: string; free: string; time: string; baku: string }
> = {
  az: {
    title: (n) => `${n}, xaricdə hara qəbul ola bilərsən?`,
    question: "Xaricdə hara qəbul ola bilərsən?",
    sub: "Azərbaycan attestatı ilə {countries} universitetləri: xərc, tələblər və son tarixlər",
    free: "Pulsuz",
    time: "təxminən 20 dəqiqə",
    baku: "Azərbaycan",
  },
  ru: {
    title: (n) => `${n}, куда можно поступить за рубежом?`,
    question: "Куда можно поступить за рубежом?",
    sub: "Вузы {countries} для выпускников с азербайджанским аттестатом: стоимость, требования и сроки",
    free: "Бесплатно",
    time: "около 20 минут",
    baku: "Азербайджан",
  },
  en: {
    title: (n) => `${n}, where can you study abroad?`,
    question: "Where can you study abroad?",
    sub: "Universities in {countries} for students with an Azerbaijani school certificate: costs, requirements and deadlines",
    free: "Free",
    time: "about 20 minutes",
    baku: "Azerbaijan",
  },
};

/** Сообщение WhatsApp на языке приглашения. */
export function inviteWhatsAppText(lang: GuestLang, name: string, url: string): string {
  if (lang === "az")
    return `Salam, ${name}! Bu, Yolutap-dır. Sənin üçün xaricdə pulsuz universitet seçimi hazırlamışıq: ~20 dəqiqəyə Azərbaycan attestatı ilə hara real qəbul ola biləcəyini, bunun nə qədər başa gəldiyini və son müraciət tarixlərini görəcəksən. Keçid şəxsidir: ${url}`;
  if (lang === "en")
    return `Hi ${name}! This is Yolutap. We've set up a free university match for you: in about 20 minutes you'll see where you can realistically get in with an Azerbaijani certificate, what it costs and the deadlines. Your personal link: ${url}`;
  return `${name}, привет! Это Yolutap. Подготовили для тебя бесплатный подбор вузов за рубежом: за ~20 минут увидишь, куда реально поступить с азербайджанским аттестатом, сколько это стоит и до какого числа подавать. Ссылка именная: ${url}`;
}
export function inviteWhatsAppHref(lang: GuestLang, name: string, url: string): string {
  return `https://wa.me/?text=${encodeURIComponent(inviteWhatsAppText(lang, name, url))}`;
}
